import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Coins } from "lucide-react";

interface CreditBalanceResponse {
  credits: number;
  nextRefillDate: string | null;
}

interface CreditWallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  action: string;
  cost: number;
}

function getDaysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const refillMs = new Date(dateStr).getTime();
  const now = Date.now();
  if (isNaN(refillMs) || refillMs <= now) return null;
  return Math.max(1, Math.ceil((refillMs - now) / (1000 * 60 * 60 * 24)));
}

export function CreditWallDialog({ isOpen, onClose, action, cost }: CreditWallDialogProps) {
  const { t } = useTranslation();

  const { data: balanceData } = useQuery<CreditBalanceResponse>({
    queryKey: ["/api/credits/balance"],
    enabled: isOpen,
  });

  const daysUntilRefill = getDaysUntil(balanceData?.nextRefillDate);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="credit-wall-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" aria-hidden="true" />
            {t("credits.wall.title", "You're out of credits")}
          </DialogTitle>
          <DialogDescription className="space-y-2 pt-1">
            <span className="block">
              <span data-testid="credit-wall-action">{action}</span>
              {" "}
              {t("credits.wall.costs", "costs")}{" "}
              <span data-testid="credit-wall-cost" className="font-medium">{cost}</span>
              {" "}
              {t("credits.wall.creditsWord", "credits")}.
            </span>
            {daysUntilRefill !== null ? (
              <span className="block" data-testid="credit-wall-refill">
                {t("credits.wall.refreshIn", {
                  defaultValue: "Credits refresh in {{days}} days",
                  days: daysUntilRefill,
                })}
              </span>
            ) : balanceData?.nextRefillDate ? (
              <span className="block" data-testid="credit-wall-refill">
                {t("credits.wall.refreshOn", {
                  defaultValue: "Credits refresh on {{date}}",
                  date: new Date(balanceData.nextRefillDate).toLocaleDateString(),
                })}
              </span>
            ) : (
              <span className="block" data-testid="credit-wall-refill">
                {t("credits.wall.refreshSoon", "Credits refresh monthly.")}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button onClick={onClose} className="w-full sm:w-auto">
            {t("credits.wall.gotIt", "Got it")}
          </Button>
          <Button
            variant="ghost"
            asChild
            className="w-full sm:w-auto"
            onClick={onClose}
          >
            <Link href="/settings" data-testid="credit-wall-upgrade">
              {t("credits.wall.upgrade", "Or upgrade for unlimited")}
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
